import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Mail, BookOpen, Cpu, Globe, Award, CheckCircle } from 'lucide-react';
import api from '../utils/axios';

const Engineering = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const departments = [
        { name: 'Computer Science & Engineering', icon: Cpu, desc: 'Focusing on AI, Machine Learning, and Cloud Computing.' },
        { name: 'Information Technology', icon: Globe, desc: 'Advanced software development and network security.' },
        { name: 'Electronics & Communication', icon: Award, desc: 'Next-gen VLSI design and satellite communication.' },
        { name: 'Civil Engineering', icon: BookOpen, desc: 'Sustainable infrastructure and structural excellence.' }
    ];

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/80 z-10"></div>
                <img 
                    src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2000&auto=format&fit=crop" 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Engineering"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <h1 className="text-5xl md:text-6xl font-black text-white uppercase tracking-tighter">
                        Faculty of <br /><span className="text-amber-400">Engineering</span>
                    </h1>
                    <div className="h-1.5 w-24 bg-[#cc0000] mt-6"></div>
                </div>
            </header>

            {/* Content Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                        <div className="lg:col-span-2 space-y-8">
                            <h2 className="text-3xl font-black uppercase text-slate-900 tracking-tight">Shaping the Innovators of Tomorrow</h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium">
                                Our Engineering programs are designed to bridge the gap between academic theory and industrial practice. With state-of-the-art labs and a curriculum vetted by industry leaders, we ensure our students are ready for the global stage.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
                                {departments.map((dept, i) => (
                                    <div key={i} className="p-8 border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all group">
                                        <dept.icon size={40} className="text-[#cc0000] mb-6 group-hover:scale-110 transition-transform" />
                                        <h3 className="text-xl font-black uppercase mb-4">{dept.name}</h3>
                                        <p className="text-sm text-slate-500 font-medium leading-relaxed">{dept.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-8">
                            <div className="bg-[#003366] p-10 text-white rounded-sm">
                                <h3 className="text-xl font-black uppercase mb-8 border-b border-white/10 pb-4">Key Highlights</h3>
                                <ul className="space-y-4">
                                    {['AICTE Approved', '100% Placement Support', 'Modern Labs', 'Industry Internships'].map((item, i) => (
                                        <li key={i} className="flex items-center space-x-3 text-sm font-bold uppercase tracking-wider">
                                            <CheckCircle className="text-amber-400" size={18} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button 
                                    onClick={() => setIsInquiryOpen(true)}
                                    className="w-full mt-10 bg-[#cc0000] hover:bg-white hover:text-[#cc0000] py-4 font-black uppercase tracking-widest transition-all"
                                >
                                    Apply for Admission
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer data={content} />

            {/* Admission Tab */}
            <button 
                onClick={() => setIsInquiryOpen(true)}
                className="fixed right-0 top-1/2 -translate-y-1/2 bg-[#cc0000] text-white py-6 px-3 flex flex-col items-center space-y-4 rounded-l-md shadow-2xl z-40 hover:bg-[#003366] transition-all group border-b-4 border-amber-400"
            >
                <Mail size={20} className="group-hover:scale-110 transition-transform" />
                <span className="[writing-mode:vertical-lr] rotate-180 uppercase font-black text-[10px] tracking-[4px]">Admission Enquiry</span>
            </button>

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Engineering;
